import React, { useState } from 'react';
import { Users, Search, CheckCircle } from 'lucide-react';
import api from '../utils/api';

interface Quiz {
  id: string;
  title: string;
  subject: string;
  _count?: {
    questions: number;
  };
}

interface Student {
  id: string;
  name: string;
  email: string;
}

interface QuizAssignmentProps {
  quizzes: Quiz[];
  students: Student[];
  selectedQuiz: Quiz | null;
  onAssignSuccess: () => void;
}

const QuizAssignment: React.FC<QuizAssignmentProps> = ({
  quizzes,
  students,
  selectedQuiz,
  onAssignSuccess
}) => {
  const [selectedQuizId, setSelectedQuizId] = useState(selectedQuiz?.id || '');
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getSubjectColor = (subject: string) => {
    const colors = {
      VOCABULARY: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      CLOZE: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      COMPREHENSION: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      MATHS: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
    };
    return colors[subject as keyof typeof colors] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
  };

  const handleStudentToggle = (studentId: string) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(s => s.id));
    }
  };

  const handleAssign = async () => {
    if (!selectedQuizId || selectedStudents.length === 0) {
      setError('Please select a quiz and at least one student');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post(`/quizzes/${selectedQuizId}/assign`, {
        studentIds: selectedStudents,
        dueDate: dueDate || null
      });

      setSuccess(`Quiz assigned to ${selectedStudents.length} student(s) successfully!`);
      setSelectedStudents([]);
      setTimeout(() => {
        onAssignSuccess();
      }, 1500);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to assign quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleUnassign = async () => {
    if (!selectedQuizId || selectedStudents.length === 0) {
      setError('Please select a quiz and at least one student to unassign');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.delete(`/quizzes/${selectedQuizId}/unassign`, {
        data: { studentIds: selectedStudents }
      });

      setSuccess(`Quiz unassigned from ${selectedStudents.length} student(s) successfully!`);
      setSelectedStudents([]);
      setTimeout(() => {
        onAssignSuccess();
      }, 1500);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to unassign quiz');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Quiz Selection */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Select Quiz to Assign</h3>

        {quizzes.length === 0 ? (
          <p className="text-gray-600 dark:text-gray-300">No quizzes available. Create a quiz first.</p>
        ) : (
          <div className="grid gap-4">
            {quizzes.map((quiz) => (
              <label
                key={quiz.id}
                className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  selectedQuizId === quiz.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                <input
                  type="radio"
                  name="quiz"
                  value={quiz.id}
                  checked={selectedQuizId === quiz.id}
                  onChange={(e) => setSelectedQuizId(e.target.value)}
                  className="mr-4"
                />
                
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-1">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100">{quiz.title}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSubjectColor(quiz.subject)}`}>
                      {quiz.subject}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {quiz._count?.questions || 0} questions
                  </p>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Student Selection */}
      {selectedQuizId && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Select Students</h3>
            {filteredStudents.length > 0 && (
              <button
                onClick={handleSelectAll}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium text-sm"
              >
                {selectedStudents.length === filteredStudents.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>

          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-full"
                placeholder="Search students by name or email..."
              />
            </div>
          </div>
          
          {/* Students List */}
          {filteredStudents.length === 0 ? (
            <p className="text-gray-600 dark:text-gray-300 text-center py-8">
              {searchTerm ? 'No students found matching your search.' : 'No students available.'}
            </p>
          ) : (
            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredStudents.map((student) => (
                <label
                  key={student.id}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedStudents.includes(student.id)
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedStudents.includes(student.id)}
                    onChange={() => handleStudentToggle(student.id)}
                    className="mr-3"
                  />
                  
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">{student.name}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{student.email}</p>
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* Selection Summary */}
          {selectedStudents.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900 rounded-lg">
              <p className="text-blue-800 dark:text-blue-200 font-medium">
                {selectedStudents.length} student{selectedStudents.length !== 1 ? 's' : ''} selected
              </p>
            </div>
          )}
        </div>
      )}

      {/* Due Date (Optional) */}
      {selectedQuizId && selectedStudents.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Assignment Settings</h3>
          <div>
            <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Due Date (Optional)
            </label>
            <input
              type="datetime-local"
              id="dueDate"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="input w-full max-w-sm"
              min={new Date().toISOString().slice(0, 16)}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Leave empty for no due date
            </p>
          </div>
        </div>
      )}

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 rounded-lg p-4 flex items-center">
          <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
          <p className="text-green-800 dark:text-green-200">{success}</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Action Buttons */}
      {selectedQuizId && selectedStudents.length > 0 && (
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleUnassign}
            disabled={loading}
            className="btn text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                Unassigning...
              </>
            ) : (
              <>
                <Users className="w-4 h-4 mr-1" />
                Unassign from {selectedStudents.length} student{selectedStudents.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
          
          <button
            onClick={handleAssign}
            disabled={loading}
            className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Assigning...
              </>
            ) : (
              <>
                <Users className="w-4 h-4 mr-2" />
                Assign Quiz to {selectedStudents.length} Student{selectedStudents.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default QuizAssignment;